"use client"

import { useState, useEffect } from "react"
import axios from "axios"
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Button,
  Avatar,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Divider,
} from "@mui/material"
import {
  Home as HomeIcon,
  CalendarToday as CalendarTodayIcon,
  Notifications as NotificationsIcon,
  AccountCircle as AccountCircleIcon,
  ExitToApp as ExitToAppIcon,
  Payment as PaymentIcon,
  Refresh as RefreshIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
} from "@mui/icons-material"
import { useNavigate } from "react-router-dom"
import { toast, ToastContainer } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"

const MesSeances = () => {
  const navigate = useNavigate()
  const [seances, setSeances] = useState([])
  const baseUrl = process.env.REACT_APP_APP_URL
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Get current user from localStorage
  const user = JSON.parse(localStorage.getItem("user"))
  const userId = user ? user.id : null

  useEffect(() => {
    fetchSeances()
  }, [])

  const fetchSeances = async () => {
    setLoading(true)
    const token = localStorage.getItem("CC_Token")

    if (!token) {
      setError("Vous n'êtes pas connecté. Veuillez vous connecter pour voir vos séances.")
      setLoading(false)
      return
    }

    try {
      const response = await axios.get(`${baseUrl}/api/seances`, { 
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      console.log("Seances:", response.data)
      // Filter seances for the current user
      const userSeances = userId ? response.data.filter((seance) => seance.user_id === userId) : []

      setSeances(userSeances)
      setLoading(false)
    } catch (error) {
      console.error("Error fetching seances:", error)
      setError("Erreur lors de la récupération des séances. Veuillez réessayer plus tard.")
      setLoading(false)
      toast.error("Erreur lors de la récupération des séances.", {
        position: "top-right",
        autoClose: 3000,
      })
    }
  }

  const handleNavigateToNotifications = () => {
    navigate("/notifications"); // Redirige vers la page notificationsdd
  };
  const handleProfile = () => {
    navigate("/Profile");
  };

  const handleLogout = () => {
    localStorage.removeItem("CC_Token")
    localStorage.removeItem("user")
    toast.success("Vous avez été déconnecté avec succès.", {
      position: "top-right",
      autoClose: 3000,
    })
    setTimeout(() => navigate("/login"), 4000)
  }



  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    })
  }

  // Format time for display
  const formatTime = (timeString) => {
    if (!timeString) return "Heure non spécifiée"

    const date = new Date(timeString)
    return date.toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  return (
    <div className="app">
      <ToastContainer />

      <AppBar position="fixed" sx={{ backgroundColor: "#003366" }}>
        <Toolbar>
          <Avatar
            alt="Logo"
            src={"../assets/cej.png"}
            sx={{ width: 40, height: 40, marginRight: "0.5rem" }}
          />
          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              fontSize: { xs: "1rem", sm: "1.25rem" },
              color: "#ffffff",
            }}
          >
            Club Équestre Jafoura
          </Typography>
          <IconButton color="inherit" onClick={handleNavigateToNotifications}>
            <NotificationsIcon />
          </IconButton>
          <IconButton color="inherit" onClick={handleProfile}>
            <AccountCircleIcon />
          </IconButton>
          <IconButton color="inherit" onClick={handleLogout}>
            <ExitToAppIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      
      <Box
        sx={{
          padding: "1rem",
          backgroundColor: "#f8f8f8",
          borderRadius: "8px",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          marginTop: "64px",
          marginBottom: "56px",
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: "600", textAlign: "center", color: "#333", marginBottom: "1.5rem" }}>
          Mes Séances
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "flex-end", marginBottom: "1.5rem" }}>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={fetchSeances}
            sx={{ backgroundColor: "#004080", borderRadius: "5px", padding: "0.5rem 1rem", fontSize: "0.875rem" }}
          >
            Rafraîchir
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Paper
            sx={{
              padding: "1rem",
              backgroundColor: "#fff3f3",
              borderRadius: "8px",
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
            }}
          >
            <Typography variant="body1" color="error" sx={{ textAlign: "center" }}>
              {error}
            </Typography>
          </Paper>
        ) : seances.length === 0 ? (
          <Paper
            sx={{
              padding: "1rem",
              backgroundColor: "#ffffff",
              borderRadius: "8px",
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
            }}
          >
            <Typography variant="body1" sx={{ textAlign: "center" }}>
              Vous n'avez pas de séances programmées.
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {seances.map((seance) => (
              <Grid item xs={12} sm={6} md={4} key={seance.id}>
                <Card
                  sx={{
                    borderRadius: "8px",
                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                    transition: "transform 0.2s",
                    "&:hover": {
                      transform: "translateY(-5px)",
                      boxShadow: "0px 6px 16px rgba(0, 0, 0, 0.15)",
                    },
                  }}
                >
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "1rem",
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: "600" }}>
                        Séance 
                      </Typography>
                      <Chip
                        label={formatDate(seance.date)}
                        color="primary"
                        size="small"
                        sx={{ backgroundColor: "#003366" }}
                      />
                    </Box>

                    <Divider sx={{ marginBottom: "1rem" }} />

                    

                    {seance.coach && (
                      <Box sx={{ display: "flex", alignItems: "center", marginBottom: "0.5rem" }}>
                        <PersonIcon sx={{ marginRight: "0.5rem", color: "#666" }} />
                        <Typography variant="body1">Coach: {seance.coach.name}</Typography>
                      </Box>
                    )}

                    <Box sx={{ marginTop: "1rem" }}>
                      <Typography variant="body2" color="text.secondary">
                        Statut: <Chip label="Confirmé" size="small" color="success" />
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Footer */}
            <Box sx={{ position: "fixed", bottom: 0, width: "100%" }}>
              <BottomNavigation
                sx={{
                  backgroundColor: "#003366",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <BottomNavigationAction
                  label="Calendrier"
                  icon={<CalendarTodayIcon sx={{ fontSize: 30, color: "#ffffff" }} />}
                  sx={{ color: "#ffffff", minWidth: "auto" }}
                  onClick={() => navigate("/emploi")}
                />
                <BottomNavigationAction
                  label="Home"
                  icon={<HomeIcon sx={{ fontSize: 30, color: "#ffffff" }} />}
                  sx={{ color: "#ffffff", minWidth: "auto" }}
                  onClick={() => navigate("/home")}
                />
                <BottomNavigationAction
                  label="Paiement"
                  icon={<PaymentIcon sx={{ fontSize: 30, color: "#ffffff" }} />}
                  sx={{ color: "#ffffff", minWidth: "auto" }}
                  onClick={() => navigate("/payment")}
                />
              </BottomNavigation>
            
      </Box>
    </div>
  )
}

export default MesSeances

